'use client';

import type { ReactNode } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Paper from '@mui/material/Paper';
import type { SxProps, Theme } from '@mui/material/styles';

interface BlogContentProps {
  children: string;
  sx?: SxProps<Theme>;
}

/**
 * BlogContent - Renders markdown content with blog-appropriate MUI styling.
 * Uses full-size headings and proper typography for long-form blog content,
 * unlike MarkdownContent which is optimized for chat context.
 */
export function BlogContent({ children, sx }: BlogContentProps) {
  return (
    <Box
      sx={{
        '& > *:first-of-type': { mt: 0 },
        '& > *:last-child': { mb: 0 },
        wordBreak: 'break-word',
        lineHeight: 1.8,
        ...sx,
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkGfm]}
        components={{
          h1: ({ children: c }) => (
            <Typography
              variant="h3"
              component="h1"
              sx={{ fontWeight: 700, mt: 4, mb: 2 }}
            >
              {c}
            </Typography>
          ),
          h2: ({ children: c }) => (
            <Typography
              variant="h4"
              component="h2"
              id={generateId(c)}
              sx={{ fontWeight: 600, mt: 4, mb: 2 }}
            >
              {c}
            </Typography>
          ),
          h3: ({ children: c }) => (
            <Typography
              variant="h5"
              component="h3"
              id={generateId(c)}
              sx={{ fontWeight: 600, mt: 3, mb: 1.5 }}
            >
              {c}
            </Typography>
          ),
          h4: ({ children: c }) => (
            <Typography
              variant="h6"
              component="h4"
              sx={{ fontWeight: 600, mt: 2, mb: 1 }}
            >
              {c}
            </Typography>
          ),
          p: ({ children: c }) => (
            <Typography
              variant="body1"
              sx={{ mb: 2, fontSize: '1.1rem', lineHeight: 1.8 }}
            >
              {c}
            </Typography>
          ),
          a: ({ href, children: c }) => (
            <Link
              href={href}
              target={href?.startsWith('http') ? '_blank' : undefined}
              rel={href?.startsWith('http') ? 'noopener noreferrer' : undefined}
              underline="hover"
              color="primary"
            >
              {c}
            </Link>
          ),
          pre: ({ children: c }) => (
            <Box
              component="pre"
              sx={{
                bgcolor: 'grey.100',
                borderRadius: 2,
                p: 2.5,
                my: 2,
                overflowX: 'auto',
                fontSize: '0.9em',
                fontFamily: 'monospace',
                border: 1,
                borderColor: 'grey.200',
              }}
            >
              {c}
            </Box>
          ),
          code: ({ children: c, ...props }) => {
            const isBlock =
              props.node?.position?.start.line !==
                props.node?.position?.end.line ||
              (props.className && props.className.includes('language-'));

            if (isBlock) {
              return <code>{c}</code>;
            }

            return (
              <Box
                component="code"
                sx={{
                  bgcolor: 'grey.100',
                  borderRadius: 0.5,
                  px: 0.75,
                  py: 0.25,
                  fontSize: '0.9em',
                  fontFamily: 'monospace',
                }}
              >
                {c}
              </Box>
            );
          },
          ul: ({ children: c }) => (
            <Box component="ul" sx={{ pl: 3, my: 1.5 }}>
              {c}
            </Box>
          ),
          ol: ({ children: c }) => (
            <Box component="ol" sx={{ pl: 3, my: 1.5 }}>
              {c}
            </Box>
          ),
          li: ({ children: c }: { children?: ReactNode }) => (
            <Typography
              component="li"
              variant="body1"
              sx={{ mb: 0.5, fontSize: '1.1rem', lineHeight: 1.8 }}
            >
              {c}
            </Typography>
          ),
          blockquote: ({ children: c }) => (
            <Box
              component="blockquote"
              sx={{
                borderInlineStart: '4px solid',
                borderColor: 'primary.main',
                pl: 2.5,
                py: 1,
                my: 2,
                bgcolor: 'grey.50',
                borderRadius: '0 8px 8px 0',
              }}
            >
              {c}
            </Box>
          ),
          hr: () => (
            <Box
              component="hr"
              sx={{
                border: 'none',
                borderTop: 1,
                borderColor: 'divider',
                my: 4,
              }}
            />
          ),
          strong: ({ children: c }) => <strong>{c}</strong>,
          em: ({ children: c }) => <em>{c}</em>,
          table: ({ children: c }) => (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{ my: 3, border: 1, borderColor: 'divider' }}
            >
              <Table size="small">{c}</Table>
            </TableContainer>
          ),
          thead: ({ children: c }) => (
            <TableHead sx={{ bgcolor: 'grey.50' }}>{c}</TableHead>
          ),
          tbody: ({ children: c }) => <TableBody>{c}</TableBody>,
          tr: ({ children: c }) => <TableRow>{c}</TableRow>,
          th: ({ children: c }) => (
            <TableCell sx={{ fontWeight: 600 }}>{c}</TableCell>
          ),
          td: ({ children: c }) => <TableCell>{c}</TableCell>,
          img: ({ src, alt }) => (
            <Box
              component="img"
              src={src}
              alt={alt || ''}
              sx={{
                maxWidth: '100%',
                height: 'auto',
                borderRadius: 2,
                my: 2,
              }}
            />
          ),
        }}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
}

function extractText(node: ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join('');
  if (node && typeof node === 'object' && 'props' in node) {
    return extractText((node as { props: { children?: ReactNode } }).props.children);
  }
  return '';
}

function generateId(children: ReactNode): string {
  const text = extractText(children);
  if (!text) return '';
  return text
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}
